import { useState, useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  type LexicalCommand,
  createCommand,
  $insertNodes,
  $createParagraphNode,
  $getRoot,
} from 'lexical';
import { $isHeadingNode, $createHeadingNode } from '@lexical/rich-text';
import { $isQuoteNode, $createQuoteNode } from '@lexical/rich-text';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { TOGGLE_LINK_COMMAND, $isLinkNode } from '@lexical/link';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $createImageNode } from './ImageNode';
import { EMOJI_LIST } from '../../types';

export const INSERT_IMAGE_COMMAND: LexicalCommand<{ src: string; altText: string }> =
  createCommand('INSERT_IMAGE_COMMAND');

interface ToolbarState {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isLink: boolean;
  blockType: string;
}

interface Props {
  onImageRequest: () => void;
}

const ToolBtn = ({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer text-[13px] ${
      active ? 'bg-violet-600/40 text-violet-200' : 'bg-white/5 hover:bg-white/15 text-white/60 hover:text-white'
    }`}
  >
    {children}
  </button>
);

export const ToolbarPlugin = ({ onImageRequest }: Props) => {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<ToolbarState>({
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isLink: false,
    blockType: 'paragraph',
  });
  const [showEmojis, setShowEmojis] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const emojiRef = useRef<HTMLDivElement>(null);

  const updateState = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root'
        ? anchorNode
        : anchorNode.getTopLevelElementOrThrow();

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      let blockType = 'paragraph';
      if (elementDOM) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          blockType = parentList ? parentList.getListType() : element.getListType();
        } else if ($isHeadingNode(element)) {
          blockType = element.getTag();
        } else if ($isQuoteNode(element)) {
          blockType = 'quote';
        }
      }

      const node = selection.anchor.getNode().getParent();
      setState({
        isBold: selection.hasFormat('bold'),
        isItalic: selection.hasFormat('italic'),
        isUnderline: selection.hasFormat('underline'),
        isLink: $isLinkNode(node) || $isLinkNode(selection.anchor.getNode()),
        blockType,
      });
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(updateState);
    });
  }, [editor, updateState]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmojis(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const formatBlock = (type: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getTopLevelElementOrThrow();

      if (type === 'paragraph') {
        if ($isHeadingNode(element) || $isQuoteNode(element)) {
          const p = $createParagraphNode();
          element.replace(p);
        }
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else if (type === 'h2') {
        const heading = $createHeadingNode('h2');
        element.replace(heading);
      } else if (type === 'h3') {
        const heading = $createHeadingNode('h3');
        element.replace(heading);
      } else if (type === 'quote') {
        const quote = $createQuoteNode();
        element.replace(quote);
      } else if (type === 'bullet') {
        if (state.blockType === 'bullet') {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
      } else if (type === 'number') {
        if (state.blockType === 'number') {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        }
      }
    });
  };

  const insertEmoji = (emoji: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertText(emoji);
      }
    });
    setShowEmojis(false);
  };

  const applyLink = () => {
    if (!linkUrl) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: linkUrl, target: '_blank' });
    }
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.formatText('bold', false as unknown as string);
        selection.formatText('italic', false as unknown as string);
        selection.formatText('underline', false as unknown as string);
      }
    });
  };

  return (
    <div className="flex items-center gap-0.5 flex-wrap p-2 bg-white/5 border-b border-white/10 rounded-t-xl">
      {/* Undo / Redo */}
      <ToolBtn onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} title="Desfazer">
        <i className="ri-arrow-go-back-line" />
      </ToolBtn>
      <ToolBtn onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} title="Refazer">
        <i className="ri-arrow-go-forward-line" />
      </ToolBtn>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Block type */}
      <ToolBtn active={state.blockType === 'paragraph'} onClick={() => formatBlock('paragraph')} title="Parágrafo">
        <i className="ri-paragraph" />
      </ToolBtn>
      <ToolBtn active={state.blockType === 'h2'} onClick={() => formatBlock('h2')} title="Título">
        <span className="text-[11px] font-800">H2</span>
      </ToolBtn>
      <ToolBtn active={state.blockType === 'h3'} onClick={() => formatBlock('h3')} title="Subtítulo">
        <span className="text-[11px] font-800">H3</span>
      </ToolBtn>
      <ToolBtn active={state.blockType === 'quote'} onClick={() => formatBlock('quote')} title="Citação">
        <i className="ri-double-quotes-l" />
      </ToolBtn>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Inline formatting */}
      <ToolBtn active={state.isBold} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')} title="Negrito (Ctrl+B)">
        <strong>B</strong>
      </ToolBtn>
      <ToolBtn active={state.isItalic} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')} title="Itálico (Ctrl+I)">
        <em>I</em>
      </ToolBtn>
      <ToolBtn active={state.isUnderline} onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')} title="Sublinhado (Ctrl+U)">
        <span style={{ textDecoration: 'underline' }}>U</span>
      </ToolBtn>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Lists */}
      <ToolBtn active={state.blockType === 'bullet'} onClick={() => formatBlock('bullet')} title="Lista de bullet">
        <i className="ri-list-unordered" />
      </ToolBtn>
      <ToolBtn active={state.blockType === 'number'} onClick={() => formatBlock('number')} title="Lista numerada">
        <i className="ri-list-ordered" />
      </ToolBtn>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Link */}
      <div className="relative">
        <ToolBtn active={state.isLink || showLinkInput} onClick={() => { setShowLinkInput(!showLinkInput); setLinkUrl(''); }} title="Link">
          <i className="ri-link" />
        </ToolBtn>
        {showLinkInput && (
          <div className="absolute top-9 left-0 z-30 bg-[#1a1a2e] border border-white/10 rounded-xl p-3 shadow-xl flex gap-2 w-72">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setShowLinkInput(false); }}
              placeholder="https://..."
              autoFocus
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-[12px] placeholder-white/20 focus:outline-none"
            />
            <button type="button" onMouseDown={(e) => { e.preventDefault(); applyLink(); }}
              className="px-2.5 py-1.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-white text-[12px] font-600 cursor-pointer whitespace-nowrap transition-colors">
              OK
            </button>
          </div>
        )}
      </div>

      {/* Clear formatting */}
      <ToolBtn onClick={clearFormatting} title="Limpar formatação">
        <i className="ri-format-clear" />
      </ToolBtn>

      <div className="w-px h-5 bg-white/10 mx-1" />

      {/* Emoji */}
      <div className="relative" ref={emojiRef}>
        <ToolBtn onClick={() => setShowEmojis(!showEmojis)} title="Inserir emoji">
          <span>😊</span>
        </ToolBtn>
        {showEmojis && (
          <div className="absolute top-9 left-0 z-30 bg-[#1a1a2e] border border-white/10 rounded-xl p-3 grid grid-cols-10 gap-0.5 shadow-xl w-72">
            {EMOJI_LIST.map((e) => (
              <button
                key={e}
                type="button"
                onMouseDown={(ev) => { ev.preventDefault(); insertEmoji(e); }}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-white/10 cursor-pointer text-[14px] transition-colors"
              >{e}</button>
            ))}
          </div>
        )}
      </div>

      {/* Image */}
      <ToolBtn onClick={onImageRequest} title="Inserir imagem">
        <i className="ri-image-add-line" />
      </ToolBtn>
    </div>
  );
};
